<template>
  <div
    id="loyalty-hub-summary-section"
    class="grid-y padding-m padding-left-s padding-right-s header page-header lh-container-custom-gutters"
    :class="[loyaltyColor, tierName?.toLowerCase() === 'bronze' ? 'margin-bottom-m' : 'margin-bottom-xs']"
  >
    <div class="cell margin-bottom-m">
      <div :class="loyaltyIcon"></div>
      <span
        class="large loyalist-title"
        :class="loyaltyTitleColor"
        v-html="headerTitle"
      ></span>
    </div>
    <div
      v-if="isLoyaltyGlobalError"
      class="cell grid-x"
    >
      <div class="cell small-12 medium-12 mlarge-5 margin-bottom-l mlarge-margin-bottom-z xlarge-margin-bottom-z mlarge-padding-right-xl">
        <h2
          class="margin-bottom-xxs title-size"
          v-html="pageHeaderTitle(firstName)"
        ></h2>
        <div class="medium rewards-icon">
          {{ earnRateContent }}
          <span
            class="custom-alignment"
            :class="brand === 'mcom' ? 'tool-tip-dark-small' : 'tooltip-small'"
            role="button"
            tabindex="0"
            :aria-label="MODAL_CONTENT.EARN_RATE_TITLE"
            @click.stop="openModal('earnRateModal', MODAL_CONTENT.EARN_RATE_TITLE, 'earnRate')"
            @keydown.enter.prevent.stop="openModal('earnRateModal', MODAL_CONTENT.EARN_RATE_TITLE, 'earnRate')"
            @keydown.space.prevent.stop="openModal('earnRateModal', MODAL_CONTENT.EARN_RATE_TITLE, 'earnRate')"
          ></span>
        </div>
        <div class="margin-top-xs small">
          <a
            href="#"
            class="link-sm margin-right-xs"
            @click.prevent.stop="openModal('howItWorksModal', PAGE_HEADER.HOW_IT_WORKS, 'howItWorks', true)"
            >{{ PAGE_HEADER.HOW_IT_WORKS }}</a
          >
          <a
            v-if="PAGE_HEADER.IS_LOYALTY_LINK"
            href="#"
            class="link-sm"
            @click.prevent.stop="openModal('loyaltyIdModal', 'Loyallist ID', 'loyaltyId')"
            >{{ PAGE_HEADER.LOYALLIST_ID }}</a
          >
        </div>
      </div>
      <div class="cell small-12 medium-6 mlarge-3">
        <h5>{{ PAGE_HEADER.REWARDS_TITLE }}</h5>
        <div
          v-if="isNotRewardsError"
          class="grid-x margin-top-xs content-space"
        >
          <div class="cell small-8 medium-8 mlarge-6 medium small-padding-right-xs medium-padding-right-z large-padding-right-z">
            <span
              @click="handleClickToPointScroll($event, emit)"
              v-html="rewardsHeading"
            ></span>
            <div
              v-if="expiryRewardMoney?.length && expiryDate?.length"
              class="margin-top-xxs small no-wrap"
              :class="{ 'rewards-sale': expirySoon }"
            >
              ${{ expiryRewardMoney + ' ' + PAGE_HEADER.EXPIRES + ' ' + formattedExpiryDate }}
            </div>
            <div class="margin-top-xs small no-wrap">
              <a
                href="#"
                class="link-sm margin-right-xs"
                @click.prevent.stop="openModal('rewardsModal', PAGE_HEADER.TRACK_REWARDS, 'RewardsModal', true)"
                >{{ PAGE_HEADER.VIEW_REWARDS }}</a
              >
              <a
                href="#"
                class="link-sm shop-now"
                @click.prevent.stop="shopNowButtonHandler"
                >{{ PAGE_HEADER.SHOP_NOW }}</a
              >
            </div>
          </div>
          <div class="cell small-3 medium-4 mlarge-4 content-end">
            <span class="dollar-icon"><h5>$</h5></span>
            <h3>
              {{ sumOfSRCardsBalance }}
            </h3>
          </div>
        </div>
        <div
          v-else
          class="grid-x margin-top-xs content-space"
        >
          <div class="medium">
            {{ PAGE_HEADER.REWARDS_ERROR_MESSAGE }}
          </div>
        </div>
      </div>
      <div
        class="cell small-12 medium-1 mlarge-1 small-margin-top-s small-margin-bottom-s medium-margin-top-z medium-margin-bottom-z content-seperator"
      >
        <div class="medium-margin-left-s medium-margin-right-s dashboard-seperator"></div>
      </div>
      <div class="cell small-12 medium-5 mlarge-3">
        <h5>{{ PAGE_HEADER.POINTS_TITLE }}</h5>
        <div class="grid-x margin-top-xs content-space">
          <div class="cell small-7 medium-8 mlarge-6 medium large-padding-right-s mlarge-padding-right-z">
            <div
              class="rewards-icon"
              :class="{ 'padding-top-xxs': balanceRewards < 0 }"
            >
              {{ rewardsToNextAwardContent }}
              <span
                v-if="balanceRewards < 0"
                class="tooltip-small"
                role="button"
                tabindex="0"
                aria-label="Negative points balance"
                @click.stop="openModal('negativePointsModal', 'Negative points balance', 'negativePoints')"
                @keydown.enter.prevent.stop="openModal('negativePointsModal', 'Negative points balance', 'negativePoints')"
                @keydown.space.prevent.stop="openModal('negativePointsModal', 'Negative points balance', 'negativePoints')"
              ></span>
            </div>
            <div class="margin-top-xs small">
              <a
                href="#"
                class="link-sm"
                @click.prevent.stop="openModal('pendingModal', PAGE_HEADER.PENDING_POINTS, 'pendingPoints', true)"
                >{{ PAGE_HEADER.PENDING_POINTS }}</a
              >
            </div>
          </div>
          <div class="cell small-4 medium-4 mlarge-5 content-end">
            <LoyaltyTracker
              :value="balanceRewards"
              :max="rewardThresholdForAward"
              size="medium"
              :tier="loyaltyTracker"
              :format-values="PAGE_HEADER.LT_FORMAT_VALUES"
            />
          </div>
        </div>
      </div>
    </div>
    <div
      v-else
      class="cell grid-x page-header-error-state"
    >
      <p
        :class="brand === 'mcom' ? 'medium' : ''"
        class="body"
      >
        {{ PAGE_HEADER.HEADER_REWARDS_ERROR }}
      </p>
    </div>
  </div>
</template>

<script setup>
import LoyaltyTracker from '@nuxt-common/atomic-ui/app/components/loyaltyTracker/LoyaltyTracker.vue';
import { computed, onMounted } from 'vue';
import { useDateFormat } from '@vueuse/core';
import { PAGE_HEADER, TIER_NAME, MODAL_CONTENT, brand } from '../../config';
import { handleClickToPointScroll } from '../../utils/scrollToPointUtil';
import eventAnalyticsTagging from '../../utils/eventsTagUtil';
import { useLoyaltyHubStore } from '../../stores/useLoyaltyHubStore.js';
import { useEnvPropsStore } from '../../stores/useEnvPropsStore';
import { useNavViewTypeStore } from '../../stores/useNavViewTypeStore';
import { useModalOverlayStore } from '../../stores/useModalOverlayStore';
import { loadOverlayComponent } from '../../utils/loadOverlayComponent';

const emit = defineEmits(['scroll-to-exclusion-details']);

const loyaltyHub = useLoyaltyHubStore();
const envPropsStore = useEnvPropsStore();
const navViewTypeStore = useNavViewTypeStore();
const modalOverlayStore = useModalOverlayStore();

const deviceType = computed(() => envPropsStore.headerContext?.deviceType || 'DESKTOP');
const deviceViewType = computed(() => navViewTypeStore.viewType || 'DESKTOP');
const summary = computed(() => loyaltyHub.unifiedLoyalty?.points?.loyaltyPointsResponse?.loyaltyInfo || {});
const summaryError = computed(() => loyaltyHub.unifiedLoyalty?.points?.loyaltyPointsResponse?.serviceErrors || {});
const unifiedRewardCards = computed(() => loyaltyHub?.unifiedLoyalty?.unifiedRewardCards);

const tierName = computed(() => loyaltyHub.tierName);

const profile = computed(() => summary.value.profile || {});
const firstName = computed(() => profile.value.firstName);
const programs = computed(() => summary.value.programs);
const tenderDetails = computed(() => summary.value.tenderDetails);
const loyaltyId = computed(() => summary.value.id);

const rewardsToNextAward = computed(() => programs.value?.[0]?.rewardsToNextAward || 0);
const rewardThresholdForAward = computed(() => programs.value?.[0]?.rewardThresholdForAward || 0);
const balanceRewards = computed(() => programs.value?.[0]?.rewards?.[0]?.balances?.[0]?.availableRewards || 0);

const sumOfSRCardsBalance = computed(() => unifiedRewardCards.value?.sumOfSRCardsBalance);
const rewardCards = computed(() => unifiedRewardCards.value?.rewardCards);
const rewardsError = computed(() => unifiedRewardCards.value?.errors);
const expiryRewardMoney = computed(() => rewardCards.value?.[0]?.rewardMoney || '');
const expiryDate = computed(() => rewardCards.value?.[0]?.expiryDate);
const expirySoon = computed(() => rewardCards.value?.[0]?.expirySoon);

const formattedExpiryDate = useDateFormat(expiryDate, 'MM/DD/YY');

const isLoyaltyGlobalError = computed(() => {
  const progs = programs.value;
  const err = summaryError.value?.error;
  return !!(progs?.length && !(err && err.length > 0));
});

const isNotRewardsError = computed(() => !!rewardCards.value?.length || !rewardsError.value?.error?.length);

const tierDetails = computed(() => PAGE_HEADER.TIER_DETAILS[tierName.value] || {});

const loyaltyColor = computed(() => tierDetails.value.loyaltyColor);
const loyaltyIcon = computed(() => tierDetails.value.loyaltyIcon);
const loyaltyTitleColor = computed(() => tierDetails.value.loyaltyTitleColor);
const loyaltyTracker = computed(() => {
  const fromConfig = tierDetails.value.loyaltyTracker;
  if (fromConfig) return fromConfig;
  if (brand === 'mcom') {
    return { BRONZE: 'bronze', SILVER: 'silver', GOLD: 'gold', PLATINUM: 'platinum' }[tierName.value] || 'silver';
  }
  return { LOYALLIST: 'loyallist', TOL: 'tol', UNL: 'tolunlocked' }[tierName.value] || 'tol';
});

const bccAvailableContent = computed(() => (tenderDetails.value?.bccAvailable ? tierDetails.value.content3PBCC : tierDetails.value.content3P));

const earnRateContent = computed(() => (tenderDetails.value?.bccAsDefault ? tierDetails.value.content : bccAvailableContent.value));

const headerTitle = computed(() => {
  const mapped = TIER_NAME[tierName.value];
  if (mapped == null || mapped === '') {
    return PAGE_HEADER.TITLE;
  }
  return PAGE_HEADER.HEADER_CONTENT(mapped) || PAGE_HEADER.TITLE;
});

const pageHeaderTitle = (name) => {
  const tierHeader = PAGE_HEADER.HEADER_TITLE?.[tierName.value];
  if (tierHeader?.TITLE) {
    return tierHeader.TITLE(name);
  }
  return PAGE_HEADER.TITLE;
};

const rewardsToNextAwardContent = computed(() => {
  const br = balanceRewards.value;
  const rta = rewardsToNextAward.value;
  if (br && br > 0 && rta > PAGE_HEADER.CLOSE_REWARD_POINTS_CAP) {
    return PAGE_HEADER.NEXT_REWARDS_POINTS(rta);
  }
  if (br && br > 0 && rta <= PAGE_HEADER.CLOSE_REWARD_POINTS_CAP) {
    return PAGE_HEADER.CLOSE_REWARDS_POINTS(rta);
  }
  if (br && br < 0) {
    return PAGE_HEADER.NEGATIVE_REWARDS_POINTS(br);
  }
  return PAGE_HEADER.NO_POINTS;
});

const rewardsHeading = computed(() => (sumOfSRCardsBalance.value === '0.00' ? PAGE_HEADER.NO_REWARDS_HEADING : PAGE_HEADER.REWARDS_HEADING));

// mutation
const toggleModal = async (val) => {
  if (modalOverlayStore.showModal[val]) {
    modalOverlayStore.close(val);
  } else {
    modalOverlayStore.toggle(val);
  }
};
const setOverlay = async (val) => {
  modalOverlayStore.setModalOverlay(val);
};

const openModal = async (idVal, titleVal, componentPath, isTagging = false) => {
  const component = await loadOverlayComponent(componentPath);
  await setOverlay({
    width: deviceType.value?.toUpperCase() === 'DESKTOP' && deviceViewType.value?.toUpperCase() === 'DESKTOP' ? '46%' : '85%',
    id: idVal,
    title: titleVal,
    component,
  });
  await toggleModal(idVal);
  if (isTagging) {
    /* Event tagging for analytics tracking */
    eventAnalyticsTagging('PAGE_HEADER', idVal);
  }
};

const shopNowButtonHandler = () => {
  eventAnalyticsTagging('PAGE_HEADER', 'shopNow'); // Event tagging for analytics tracking
  window.open(window.location.origin, '_blank');
};

onMounted(async () => {
  let propObj = {};
  if (brand === 'mcom') {
    propObj = {
      customer_loyalty_id: loyaltyId.value && loyaltyId.value.includes('*') ? loyaltyId.value : 'L************',
      customer_loyalty_tier: tierName.value,
      loyalty_balance: balanceRewards.value,
      rewards_balance: sumOfSRCardsBalance.value,
    };
  } else if (brand === 'bcom') {
    propObj = {
      customer_loyalty_id: loyaltyId.value,
      customer_loyalty_tier: tierName.value,
      balance_rewards: sumOfSRCardsBalance.value,
    };
  }
  eventAnalyticsTagging('PAGE_HEADER', 'pageHeaderLoad', propObj); // Event tagging for analytics tracking on Page Load
});
</script>





<style lang="scss" scoped>
@import '@csgIcons/_tooltip-small.scss';
@import '@csgIcons/new/_tool-tip-dark-small.scss';

.loyalist-title {
  position: relative;
  bottom: rem-calc(6);
  margin-left: rem-calc(4);
  text-transform: $text-uppercase;
}
.rewards-sale {
  color: $sale-color;
}
.dashboard-seperator {
  border: $gray-3-border;
}

.content-space {
  justify-content: space-between;
}
.content-seperator {
  display: flex;
  justify-content: center;
}
.content-end {
  display: flex;
  justify-content: end;
}
.no-wrap {
  text-wrap: nowrap;
}
.dollar-icon {
  position: relative;
  top: rem-calc(3);
}
@include breakpoint(small down) {
  .content-seperator {
    display: block;
  }
}
.lh-container-custom-gutters:deep() {
  margin-right: rem-calc(20);
  margin-left: rem-calc(20);
  @include breakpoint(small down) {
    margin-right: rem-calc(0);
    margin-left: rem-calc(0);
  }
}


@import '~@core/csg/src/scss/encodedIcons/bcom/_loyallist-small.scss';
/* Rewards text and tooltip alignment as per figma */
.rewards-icon {
  position: relative;
  font-size: rem-calc(14);
  .custom-alignment {
    position: absolute;
    bottom: rem-calc(-3);
  }
}
.header {
  background: $background-color;
  border-top: rem-calc(10) solid $loyalty-1-color;
}
.loyalty-color {
  border-top: rem-calc(10) solid $loyalty-1-color;
}
.loyalty-TOL {
  border-top: rem-calc(10) solid $loyalty-bg-3-color;
}
.loyalty-UNL {
  border-top: rem-calc(10) solid $loyalty-3-color;
}
.loyalty-title-color {
  color: $loyalty-1-color;
}
.loyalty-TOL-color {
  color: $loyalty-bg-3-color;
}
.loyalty-UNL-color {
  color: $loyalty-4-color;
}
.title-size {
  overflow: hidden;
  text-overflow: ellipsis;
  text-transform: $text-uppercase;
}
</style>